import React from 'react';
import axios from 'axios';
import { Clock, Zap, Users, AlertTriangle, Trophy, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return { headers: { 'Authorization': `Bearer ${token}` } };
};

function formatTime(minutes) {
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

const BUCKET_LABELS = {
  under_5: '< 5 min',
  '5_to_15': '5-15 min',
  '15_to_60': '15-60 min',
  '1h_to_24h': '1-24 hr',
  over_24h: '> 24 hr',
};

const BUCKET_COLORS = ['#22C55E', '#84CC16', '#EAB308', '#F97316', '#EF4444'];

export function ResponseTimerWidget() {
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/leads/response-timer/stats`, getAuthHeader());
      setStats(resp.data);
    } catch {
      setStats(null);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-48 mb-4" />
        <div className="h-32 bg-gray-100 rounded" />
      </div>
    );
  }

  if (!stats) return null;

  const chartData = Object.entries(BUCKET_LABELS).map(([key, label], idx) => ({
    name: label,
    count: stats.distribution?.[key] || 0,
    color: BUCKET_COLORS[idx],
  }));

  const avgColor = stats.overall_avg_minutes < 5 ? 'text-green-600' :
    stats.overall_avg_minutes < 15 ? 'text-yellow-600' :
    stats.overall_avg_minutes < 60 ? 'text-orange-500' : 'text-red-600';

  const avgBg = stats.overall_avg_minutes < 5 ? 'from-green-500 to-emerald-600' :
    stats.overall_avg_minutes < 15 ? 'from-yellow-500 to-amber-600' :
    stats.overall_avg_minutes < 60 ? 'from-orange-500 to-red-500' : 'from-red-500 to-red-700';

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="response-timer-widget">
      {/* Header */}
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
        <Clock className="w-5 h-5 text-indigo-600" />
        <h3 className="font-bold text-gray-800">Lead Response Timer</h3>
        <span className="ml-auto text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{stats.total_responded} responded / {stats.total_leads} total</span>
      </div>

      <div className="p-5 space-y-5">
        {/* Top Metrics */}
        <div className="grid grid-cols-3 gap-3">
          {/* Avg Response Time */}
          <div className={`bg-gradient-to-br ${avgBg} rounded-xl p-4 text-white text-center`} data-testid="avg-response-time">
            <Zap className="w-5 h-5 mx-auto mb-1 opacity-80" />
            <p className="text-2xl font-bold">{formatTime(stats.overall_avg_minutes)}</p>
            <p className="text-[11px] opacity-80">Avg Response</p>
          </div>

          {/* Waiting */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 text-center border border-amber-200" data-testid="waiting-leads-count">
            <AlertTriangle className="w-5 h-5 mx-auto mb-1 text-amber-500" />
            <p className="text-2xl font-bold text-amber-700">{stats.total_waiting}</p>
            <p className="text-[11px] text-amber-600">Awaiting Response</p>
          </div>

          {/* Under 5 min */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 text-center border border-green-200">
            <Trophy className="w-5 h-5 mx-auto mb-1 text-green-500" />
            <p className="text-2xl font-bold text-green-700">{stats.distribution?.under_5 || 0}</p>
            <p className="text-[11px] text-green-600">Under 5 min</p>
          </div>
        </div>

        {/* Distribution Chart */}
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Response Time Distribution</p>
          <div className="h-36">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barCategoryGap="20%">
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} width={25} />
                <Tooltip formatter={(v) => [`${v} leads`, 'Count']} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Rep Leaderboard */}
        {stats.rep_leaderboard?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Sales Rep Leaderboard
            </p>
            <div className="space-y-1.5">
              {stats.rep_leaderboard.map((rep, idx) => (
                <div key={rep.rep} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2" data-testid={`rep-row-${idx}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-700' : 'bg-gray-300'}`}>
                    {idx + 1}
                  </span>
                  <span className="font-medium text-gray-800 flex-1 truncate">{rep.rep}</span>
                  <span className={`text-xs font-semibold ${rep.avg_minutes < 5 ? 'text-green-600' : rep.avg_minutes < 15 ? 'text-yellow-600' : rep.avg_minutes < 60 ? 'text-orange-500' : 'text-red-600'}`}>
                    {formatTime(rep.avg_minutes)}
                  </span>
                  <span className="text-[10px] text-gray-400">{rep.total_responded} leads</span>
                  {rep.under_5_count > 0 && (
                    <span className="text-[10px] text-green-600 bg-green-100 px-1.5 py-0.5 rounded-full">{rep.under_5_count} fast</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Waiting Leads Alert */}
        {stats.waiting_leads?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-amber-600 mb-2 uppercase tracking-wide flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Leads Awaiting Response
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {stats.waiting_leads.map((lead) => (
                <div key={lead.lead_id} className="flex items-center gap-2 text-sm bg-amber-50 rounded-lg px-3 py-2 border border-amber-100" data-testid={`waiting-lead-${lead.lead_id}`}>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-800">{lead.name || 'Unknown'}</span>
                    {lead.lead_no && <span className="text-[10px] text-gray-400 ml-1">#{lead.lead_no}</span>}
                    <span className="text-[10px] text-gray-500 ml-2">{lead.source || ''}</span>
                  </div>
                  <span className="text-xs text-gray-500">{lead.assigned_to}</span>
                  <span className={`text-xs font-bold ${lead.elapsed_minutes > 60 ? 'text-red-600' : lead.elapsed_minutes > 15 ? 'text-orange-500' : 'text-yellow-600'}`}>
                    {formatTime(lead.elapsed_minutes)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
